import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../../../utils/cn";
import {
  AGENT_ESCALATION_MAX,
  AGENT_HANDOFF_WINDOW_MAX,
  AGENT_MAX_REPLIES_CAP,
} from "../agentPayload";
import {
  CharCounter,
  ChipRadioGroup,
  CONTROL_CLS,
  DayPills,
  SectionTitle,
  TEXTAREA_CLS,
} from "./primitives";

// Cuándo toma el control: las reglas de horario y los frenos que evitan que el
// agente se quede pegado en una conversación.

// `canUseAuto=false` (rol sin tier de administrador): el backend reserva el
// modo automático —activa datos sin revisión— para administradores, así que
// no se ofrece; si el agente ya está en automático se sigue mostrando para no
// mentir sobre su estado.
export default function BehaviorSection({ value, onChange, canUseAuto = true }) {
  const { t } = useTranslation();

  const toggleDay = (day) => {
    const days = value.business_days.includes(day)
      ? value.business_days.filter((d) => d !== day)
      : [...value.business_days, day].sort((a, b) => a - b);
    onChange({ business_days: days });
  };

  const escalationLen = (value.escalation_message || "").length;

  // La ayuda del autoaprendizaje explica el modo elegido; un valor ausente se
  // lee como "off", igual que el default del contrato. Literales en cada rama
  // (no un mapa indexado) para que las claves de traducción se vean en el código.
  const selfLearning = value.self_learning || "off";
  const selfLearningHelp =
    selfLearning === "auto"
      ? t(
          "Los datos de respuestas citadas o con código de consulta se activan solos; los demás quedan por aprobar.",
        )
      : selfLearning === "review"
        ? t(
            "Cuando tu equipo responde una consulta, la IA propone un dato y queda en Memoria aprendida esperando tu aprobación.",
          )
        : t(
            "No aprende de las consultas al equipo. Igual usa los datos ya aprobados en Memoria aprendida.",
          );

  return (
    <section className="space-y-4">
      <SectionTitle>{t("Cuándo toma el control")}</SectionTitle>

      {/* Radios nativos verticales: accesibles sin roving manual. */}
      <div role="radiogroup" aria-label={t("Cuándo responde la IA")} className="space-y-2">
        {[
          {
            value: "always",
            label: t("Siempre"),
            desc: t("Responde todos los mensajes entrantes de conversaciones sin agente."),
          },
          {
            value: "off_hours",
            label: t("Solo fuera de horario"),
            desc: t(
              "En horario de atención responde tu equipo; la IA cubre noches, fines de semana y feriados según los días que marques.",
            ),
          },
          {
            value: "first_reply",
            label: t("Solo la primera respuesta"),
            desc: t(
              "La IA da una única respuesta de bienvenida por caso y luego cede la conversación.",
            ),
          },
        ].map((opt) => (
          <label key={opt.value} className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="radio"
              name="agent-trigger"
              value={opt.value}
              checked={value.trigger === opt.value}
              onChange={() => onChange({ trigger: opt.value })}
              className="mt-0.5 !w-5 !h-5 md:!w-4 md:!h-4 accent-brand-500 shrink-0"
            />
            <span className="min-w-0">
              <span className="block text-xs font-medium text-fg">{opt.label}</span>
              <span className="block text-[12px] md:text-[11px] text-fg-muted">{opt.desc}</span>
            </span>
          </label>
        ))}
      </div>

      {/* Horario: SOLO visible con off_hours (fuera de él no se usa). */}
      {value.trigger === "off_hours" && (
        <div className="space-y-2 pl-6">
          <p className="block text-xs font-medium text-fg">
            {t("Horario de atención del equipo (America/Santiago)")}
          </p>
          <DayPills value={value.business_days} onToggle={toggleDay} />
          <div className="flex items-center gap-2">
            <label htmlFor="agent-biz-start" className="hd-field-label text-xs text-fg-muted">
              {t("de")}
            </label>
            <input
              id="agent-biz-start"
              type="time"
              value={value.business_start}
              onChange={(e) => onChange({ business_start: e.target.value })}
              className={cn(CONTROL_CLS, "!w-auto")}
            />
            <label htmlFor="agent-biz-end" className="hd-field-label text-xs text-fg-muted">
              {t("a")}
            </label>
            <input
              id="agent-biz-end"
              type="time"
              value={value.business_end}
              onChange={(e) => onChange({ business_end: e.target.value })}
              className={cn(CONTROL_CLS, "!w-auto")}
            />
          </div>
          <p className="text-[12px] md:text-[11px] text-fg-muted">
            {t(
              "La IA responde FUERA de este horario. Dentro de él guarda silencio para que responda tu equipo.",
            )}
          </p>
        </div>
      )}

      {/* Tope por caso */}
      <div className="space-y-1">
        <label
          htmlFor="agent-max-replies"
          className="hd-field-label block text-xs font-medium text-fg"
        >
          {t("Máx. respuestas de IA por caso")}
        </label>
        <input
          id="agent-max-replies"
          type="number"
          inputMode="numeric"
          min={0}
          max={AGENT_MAX_REPLIES_CAP}
          step={1}
          value={value.max_replies_per_case}
          onChange={(e) => onChange({ max_replies_per_case: e.target.value })}
          className={cn(CONTROL_CLS, "sm:!w-32")}
        />
        <p className="text-[12px] md:text-[11px] text-fg-muted">{t("0 = sin límite")}</p>
      </div>

      {/* Handoff */}
      <label className="flex items-start gap-2.5 cursor-pointer">
        <input
          type="checkbox"
          checked={value.handoff_on_agent_reply}
          onChange={(e) => onChange({ handoff_on_agent_reply: e.target.checked })}
          className="mt-0.5 !w-5 !h-5 md:!w-4 md:!h-4 accent-brand-500 shrink-0"
        />
        <span className="min-w-0">
          <span className="block text-xs font-medium text-fg">{t("Ceder al humano")}</span>
          <span className="block text-[12px] md:text-[11px] text-fg-muted">
            {t(
              "Si un agente escribe en el caso, la IA deja de intervenir aunque la conversación siga sin asignar.",
            )}
          </span>
        </span>
      </label>

      {/* Ventana de "ceder al humano": solo tiene sentido con la casilla
          marcada (sin ella la IA nunca cede por una respuesta humana). */}
      {value.handoff_on_agent_reply && (
        <div className="space-y-1 pl-6">
          <label
            htmlFor="agent-handoff-window"
            className="hd-field-label block text-xs font-medium text-fg"
          >
            {t("Ceder solo si una persona respondió en las últimas N horas")}
          </label>
          <input
            id="agent-handoff-window"
            type="number"
            inputMode="decimal"
            min={0}
            max={AGENT_HANDOFF_WINDOW_MAX}
            // Medio punto: la ventana admite fracciones (0.5 = media hora).
            step={0.5}
            value={value.handoff_window_hours}
            onChange={(e) => onChange({ handoff_window_hours: e.target.value })}
            aria-describedby="agent-handoff-window-help"
            className={cn(CONTROL_CLS, "sm:!w-32")}
          />
          <p id="agent-handoff-window-help" className="text-[12px] md:text-[11px] text-fg-muted">
            {t("0 = cualquier respuesta del caso. Máximo 168 horas (7 días); 0,5 = media hora.")}
          </p>
        </div>
      )}

      {/* Mensaje de derivación */}
      <div className="space-y-1">
        <label
          htmlFor="agent-escalation"
          className="hd-field-label block text-xs font-medium text-fg"
        >
          {t("Mensaje al derivar a un humano")}
        </label>
        <textarea
          id="agent-escalation"
          rows={2}
          value={value.escalation_message}
          maxLength={AGENT_ESCALATION_MAX}
          onChange={(e) =>
            onChange({ escalation_message: e.target.value.slice(0, AGENT_ESCALATION_MAX) })
          }
          placeholder={t(
            "Entendido, te comunico con una persona del equipo. En breve te atienden.",
          )}
          className={TEXTAREA_CLS}
        />
        <div className="flex items-center justify-between gap-2">
          <p className="text-[12px] md:text-[11px] text-fg-muted">
            {t(
              "Es lo último que dice la IA antes de apartarse cuando el contacto pide un humano.",
            )}
          </p>
          <CharCounter value={escalationLen} max={AGENT_ESCALATION_MAX} />
        </div>
      </div>

      {/* Razonamiento del proveedor: el backend solo lo aplica con DeepSeek. */}
      <div className="space-y-2">
        <span className="block text-xs font-medium text-fg">
          {t("Razonamiento")}
        </span>
        <ChipRadioGroup
          value={value.reasoning || "off"}
          options={[
            { value: "off", label: t("Apagado") },
            { value: "on", label: t("Encendido") },
          ]}
          onChange={(v) => onChange({ reasoning: v })}
          ariaLabel={t("Razonamiento")}
          describedBy="agent-reasoning-help"
        />
        <p id="agent-reasoning-help" className="text-[12px] md:text-[11px] text-fg-muted">
          {t(
            "Solo aplica a agentes con DeepSeek: piensa antes de responder, pero cada respuesta tarda más y gasta más tokens.",
          )}
        </p>
      </div>

      {/* Memoria aprendida: los datos ya aprobados se usan SIEMPRE (eso no se
          configura acá); self_learning solo decide si ESTE agente aprende de
          las respuestas del equipo a sus consultas. */}
      <div className="space-y-2">
        <span className="block text-xs font-medium text-fg">
          {t("Autoaprendizaje")}
        </span>
        <ChipRadioGroup
          value={selfLearning}
          options={[
            { value: "off", label: t("Apagado") },
            { value: "review", label: t("Con revisión") },
            ...(canUseAuto || selfLearning === "auto"
              ? [{ value: "auto", label: t("Automático") }]
              : []),
          ]}
          onChange={(v) => onChange({ self_learning: v })}
          ariaLabel={t("Autoaprendizaje")}
          describedBy="agent-self-learning-help"
        />
        <p id="agent-self-learning-help" className="text-[12px] md:text-[11px] text-fg-muted">
          {selfLearningHelp}
          {!canUseAuto && (
            <>
              {" "}
              {t("Solo un administrador puede activar el modo automático.")}
            </>
          )}
        </p>
      </div>
    </section>
  );
}
